import { Info, Moon, Sun } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useTheme } from '../contexts/ThemeContext';
import { getPageInfoContent } from '../content';
import type { AppRoute } from '../routing';

interface HeaderActionsProps {
  currentPage: AppRoute;
}

export function HeaderActions({ currentPage }: HeaderActionsProps) {
  const { themeName, setTheme } = useTheme();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const isDarkTheme = themeName === 'dark';
  const pageContent = getPageInfoContent(currentPage);

  useEffect(() => {
    if (!isInfoOpen) return;

    const closeInfoOnOutsideClick = (event: MouseEvent) => {
      if (!infoRef.current?.contains(event.target as Node)) {
        setIsInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', closeInfoOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeInfoOnOutsideClick);
  }, [isInfoOpen]);

  return (
    <div className="header-actions">
      <div className="header-info" ref={infoRef}>
        <button
          type="button"
          className={`header-action-button${isInfoOpen ? ' is-active' : ''}`}
          aria-label="About this page"
          aria-expanded={isInfoOpen}
          onClick={() => setIsInfoOpen((open) => !open)}
        >
          <Info size={17} strokeWidth={2} aria-hidden="true" />
        </button>

        {isInfoOpen && (
          <div className="header-info__popover" role="dialog" aria-label="Page information">
            <strong>{pageContent?.title ?? 'Fractal Investors'}</strong>
            {pageContent?.subtitle && <span>{pageContent.subtitle}</span>}
            <p>{pageContent?.info ?? 'Portfolio analytics and intelligence workspace.'}</p>
          </div>
        )}
      </div>

      <button
        type="button"
        className="header-action-button"
        aria-label={isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'}
        title={isDarkTheme ? 'Light theme' : 'Dark theme'}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setTheme(isDarkTheme ? 'light' : 'dark')
        }}
      >
        {isDarkTheme ? (
          <Sun size={17} strokeWidth={2} aria-hidden="true" />
        ) : (
          <Moon size={17} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
